<template>
  <div id="app" :class="{ 'app--with-tabbar': showTabbar }">
    <template v-if="isConfigLoaded">
      <router-view v-slot="{ Component }">
        <component :is="Component" />
      </router-view>

      <Teleport to="body">
        <van-tabbar
          v-if="showTabbar"
          route
          fixed
          class="custom-tabbar"
          style="z-index: 99"
        >
          <van-tabbar-item
            v-for="(item, index) in menuList"
            :key="index"
            :to="item.path"
          >
            <span>{{ item.name }}</span>
            <template #icon>
              <img
                class="tabbar-icon"
                :src="resolveTabIcon(item)"
                :alt="item.name"
              />
            </template>
          </van-tabbar-item>
        </van-tabbar>
      </Teleport>
    </template>
    <div v-else class="loading-container">
      <van-loading type="spinner" color="#1989fa" size="24px" vertical>
        加载中...
      </van-loading>
    </div>
  </div>
</template>

<script setup>
import { computed, provide, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import iconShouye from "@/assets/daohang/shouye.png";
import iconFuli from "@/assets/daohang/fuli.png";
import iconHongli from "@/assets/daohang/hongli.png";
import iconQingling from "@/assets/daohang/qingling.png";
import iconWode from "@/assets/daohang/wode.png";

const store = useStore();
const route = useRoute();

const overlayOpenCount = ref(0);

const setAppOverlayOpen = (open) => {
  if (open) {
    overlayOpenCount.value += 1;
  } else {
    overlayOpenCount.value = Math.max(0, overlayOpenCount.value - 1);
  }
};

provide("setAppOverlayOpen", setAppOverlayOpen);

const isConfigLoaded = computed(() => {
  return !!store.state.config.webViewConfig;
});

const menuList = computed(() => {
  const config = store.state.config.webViewConfig;
  const list = Array.isArray(config?.page_menu_list)
    ? [...config.page_menu_list]
    : [];
  return list
    .filter((item) => item && item.is_active !== false && item.path)
    .sort((a, b) => (a.index || 0) - (b.index || 0));
});

/** 按路由/名称映射底部导航切图 */
const TAB_ICON_BY_PATH = {
  "/": iconShouye,
  "/home": iconShouye,
  "/invite": iconFuli,
  "/stock": iconHongli,
  "/recover": iconQingling,
  "/profile": iconWode,
  "/priority-payout": iconQingling,
  "/priority-arrival": iconQingling,
  "/support": iconFuli,
};

const normalizePath = (p) => {
  const raw = (p || "").trim();
  if (!raw || raw === "/") return "/";
  return raw.replace(/\/$/, "") || "/";
};

const resolveTabIcon = (item) => {
  const path = normalizePath(item?.path);
  if (TAB_ICON_BY_PATH[path]) return TAB_ICON_BY_PATH[path];

  const name = item?.name || "";
  if (/首页|主页/.test(name)) return iconShouye;
  if (/邀请|福利/.test(name)) return iconFuli;
  if (/AI|红利|股票/.test(name)) return iconHongli;
  if (/清零|负债/.test(name)) return iconQingling;
  if (/优先|打款|到账/.test(name)) return iconQingling;
  if (/我的|个人|中心/.test(name)) return iconWode;
  if (/客服|服务/.test(name)) return iconFuli;
  if (/门票|通行/.test(name)) return iconHongli;

  return iconShouye;
};

const shouldShowTabbar = computed(() => {
  const currentPath = normalizePath(route.path);
  const inMenuList = menuList.value.some(
    (item) => normalizePath(item.path) === currentPath
  );
  const depth =
    currentPath === "/" ? 0 : currentPath.split("/").filter(Boolean).length;
  return inMenuList && depth <= 1;
});

const showTabbar = computed(
  () => shouldShowTabbar.value && overlayOpenCount.value === 0
);
</script>

<style>
#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

#app.app--with-tabbar {
  padding-bottom: 58px;
}

.custom-tabbar .van-tabbar-item {
  color: #333;
}

.custom-tabbar .van-tabbar-item--active {
  color: #2a7fff;
}

.custom-tabbar .tabbar-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
  display: block;
}

/* 覆盖 WebView 注入的 bottom 偏移，强制贴底 */
html body .van-tabbar.custom-tabbar {
  position: fixed !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  transform: none !important;
  margin: 0 !important;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}
</style>
